import { logEvent } from '../lib/analytics'
import { isOverlayBusy } from '../lib/overlayBusy'
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { posterUrl } from '../lib/universes'

// The Canon, Sealed — the ceremony for finishing an ENTIRE franchise (every show
// in a Canon, not just one series). A 6-scene sequence: a director calls "Cut!"
// on a rising set (OpenScene), the canon's title card (TitleScene), the full
// tally counting up (StatsScene), every poster developing into color
// (PostersScene), a rotating trophy sealing the moment (SealScene), and a
// closing beat suggesting where to go next (NextScene).
//
// Driven by a real RAF-based sequencer (see useSequence below), not CSS-only
// timing — each scene owns a time window and receives a 0..1 progress value
// local to that window, mirroring the seg()/ease()/lerp() helpers the design
// doc was built against (window.useScene()/interpolate/Easing don't exist in
// this app; these are plain local ports of the same math).
//
// Honors the same wrap hold contract as FinalCut (window.__nextepWrapUntil /
// nextep:wrap-done) so the two chains never stack, and the same tap-anywhere-
// to-skip UX. Reduced motion collapses the whole thing to one short static
// summary card instead of playing the sequence slower.

const FONT = 'var(--font)'
const SERIF = 'var(--font-serif)'
const AMBER = 'var(--amber)'
const GOLD = 'var(--gold-leaf)'
const PURPLE = '#a29bfe'
const BG = '#050508'

function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)) }
function ease(t) { const c = clamp(t, 0, 1); return 1 - Math.pow(1 - c, 3) }
function lerp(a, b, t) { return a + (b - a) * clamp(t, 0, 1) }
// Maps a global progress value [0,1] within a scene into a local [from,to] window -> 0..1
function seg(progress, from, to) { return clamp((progress - from) / (to - from), 0, 1) }

// ── Scene timing (ms). Total ~19.4s — long enough that tap-to-skip is essential.
const SCENES = [
  { key: 'open',    dur: 3200 },
  { key: 'title',   dur: 2900 },
  { key: 'stats',   dur: 3200 },
  { key: 'posters', dur: 3200 },
  { key: 'seal',    dur: 3500 },
  { key: 'next',    dur: 3400 },
]
const TOTAL_MS = SCENES.reduce((s, x) => s + x.dur, 0)
let acc = 0
const SCENE_STARTS = SCENES.map(s => { const start = acc; acc += s.dur; return start })

// Visual-intensity scale by canon size — same ladder idea as the old CanonSeal's
// tierFor()/PostWatchSheet's CLOSE_TIERS: a 10-show canon should visibly outrank
// a 3-show one. Applied narrowly (megaphone wave energy, poster stagger tightness,
// trophy orbit speed) rather than to every element.
function scaleFor(showCount) {
  const t = clamp(((showCount || 1) - 1) / 9, 0, 1) // 1 show -> 0, 10+ shows -> 1
  return {
    t,
    waveArcs: showCount >= 8 ? 4 : showCount >= 5 ? 3 : 2,
    posterStagger: lerp(0.16, 0.09, t), // bigger canon = tighter stagger, more energy
    orbitSpeed: lerp(1, 1.6, t),
    statSpeed: lerp(1, 1.25, t),
  }
}

// ── Sequencer clock ──────────────────────────────────────────────────────────
// Wall-clock driven (setInterval + real timestamps), not requestAnimationFrame:
// rAF is throttled to zero in backgrounded/hidden tabs in Chrome, which would
// freeze an 18s+ cinematic indefinitely if the user app-switches mid-sequence.
// setInterval keeps ticking (even if coarsely throttled) and elapsed is always
// computed from real Date.now() deltas, so the sequence self-corrects to the
// right scene the moment the tab is foregrounded again rather than drifting.
function useSequence(playing) {
  const [elapsed, setElapsed] = useState(0)
  const startRef = useRef(0)
  const intervalRef = useRef(0)
  useEffect(() => {
    if (!playing) return
    startRef.current = Date.now()
    setElapsed(0)
    const tick = () => {
      const el = Date.now() - startRef.current
      setElapsed(el)
      if (el >= TOTAL_MS) clearInterval(intervalRef.current)
    }
    intervalRef.current = setInterval(tick, 33)
    return () => clearInterval(intervalRef.current)
  }, [playing])
  let idx = SCENES.findIndex((s, i) => elapsed < SCENE_STARTS[i] + s.dur)
  if (idx === -1) idx = SCENES.length - 1
  const sceneStart = SCENE_STARTS[idx]
  const progress = clamp((elapsed - sceneStart) / SCENES[idx].dur, 0, 1)
  return { sceneKey: SCENES[idx].key, progress, elapsed, done: elapsed >= TOTAL_MS }
}

// ── Pieces ───────────────────────────────────────────────────────────────────
function Poster({ show, developed, delay }) {
  const d = clamp(seg(developed, delay, delay + 0.28), 0, 1)
  const src = posterUrl(show.poster_path, 'w185')
  return (
    <div style={{
      width: 92, height: 134, borderRadius: 12, flexShrink: 0, position: 'relative', overflow: 'hidden',
      background: src ? `url(${src}) center/cover` : 'repeating-linear-gradient(135deg, rgba(var(--ink-rgb),0.08) 0 10px, rgba(var(--ink-rgb),0.03) 10px 20px)',
      filter: `grayscale(${1 - d}) brightness(${lerp(0.35, 1, d)})`,
      border: '1px solid rgba(var(--ink-rgb),0.12)',
    }}>
      {!src && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(var(--text-rgb),0.4)', fontSize: 9, textAlign: 'center', padding: 6 }}>{show.name}</div>
      )}
      <div style={{
        position: 'absolute', top: 6, right: 6, width: 20, height: 20, borderRadius: 'var(--radius-full)',
        background: AMBER, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--amber-ink)', fontWeight: 800, fontSize: 11,
        opacity: d > 0.6 ? 1 : 0, transform: `scale(${d > 0.6 ? 1 : 0.4})`,
      }}>✓</div>
    </div>
  )
}

function DirectorsChair({ p }) {
  const rise = ease(p)
  const wood = 'linear-gradient(100deg,#c98a45 0%,#e3aa63 45%,#a86b31 100%)'
  const woodDark = 'linear-gradient(100deg,#a86b31,#7c4e22)'
  const canvas = 'linear-gradient(180deg,#8a5a3a,#6e4429)'
  return (
    <div style={{ position: 'relative', width: 156, height: 182, opacity: rise, transform: `translateY(${lerp(16, 0, rise)}px)` }}>
      <div style={{ position: 'absolute', left: 26, top: 7, width: 7, height: 130, background: wood, borderRadius: 3 }} />
      <div style={{ position: 'absolute', right: 26, top: 7, width: 7, height: 130, background: wood, borderRadius: 3 }} />
      <div style={{ position: 'absolute', left: 23, top: 0, width: 14, height: 14, borderRadius: 'var(--radius-full)', background: wood }} />
      <div style={{ position: 'absolute', right: 23, top: 0, width: 14, height: 14, borderRadius: 'var(--radius-full)', background: wood }} />
      <div style={{
        position: 'absolute', left: 30, right: 30, top: 17, height: 48, borderRadius: 4,
        background: canvas, boxShadow: `0 0 ${lerp(0, 14, rise)}px rgba(var(--amber-rgb),0.3)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ background: '#e9dfc8', color: '#4a3a24', fontSize: 8, fontWeight: 700, letterSpacing: '0.06em', padding: '2px 8px', borderRadius: 2 }}>NEXTEP</div>
      </div>
      <div style={{ position: 'absolute', left: 40, top: 102, width: 7, height: 68, background: wood, transform: 'rotate(12deg)', transformOrigin: 'top', borderRadius: 3 }} />
      <div style={{ position: 'absolute', right: 40, top: 102, width: 7, height: 68, background: wood, transform: 'rotate(-12deg)', transformOrigin: 'top', borderRadius: 3 }} />
      <div style={{ position: 'absolute', left: 52, top: 102, width: 7, height: 68, background: woodDark, transform: 'rotate(-16deg)', transformOrigin: 'top', borderRadius: 3, zIndex: 0 }} />
      <div style={{ position: 'absolute', right: 52, top: 102, width: 7, height: 68, background: woodDark, transform: 'rotate(16deg)', transformOrigin: 'top', borderRadius: 3, zIndex: 0 }} />
      <div style={{ position: 'absolute', left: '50%', top: 137, width: 10, height: 10, marginLeft: -5, borderRadius: 'var(--radius-full)', background: '#5a4020', zIndex: 2 }} />
      <div style={{ position: 'absolute', left: 10, top: 80, width: 30, height: 8, background: wood, borderRadius: 4 }} />
      <div style={{ position: 'absolute', right: 10, top: 80, width: 30, height: 8, background: wood, borderRadius: 4 }} />
      <div style={{
        position: 'absolute', left: 19, right: 19, top: 83, height: 26, zIndex: 1,
        background: canvas, borderRadius: '2px 2px 26px 26px / 2px 2px 14px 14px',
        boxShadow: 'inset 0 5px 7px rgba(0,0,0,0.35)',
      }} />
      <div style={{ position: 'absolute', left: 34, right: 34, top: 153, height: 6, background: wood, borderRadius: 3 }} />
    </div>
  )
}

function Megaphone({ p, wave, arcs }) {
  const rise = ease(p)
  const cutIn = ease(clamp((p - 0.05) / 0.35, 0, 1))
  const jitterT = clamp((p - 0.05) / 0.95, 0, 1)
  const jx = Math.sin(jitterT * 60) * 1.6 * cutIn
  const jy = Math.cos(jitterT * 47) * 1.2 * cutIn
  const jr = Math.sin(jitterT * 39) * 2.2 * cutIn
  return (
    <div style={{ position: 'relative', width: 64, height: 46, transform: `translateY(${lerp(10, 0, rise)}px) rotate(-14deg)`, opacity: rise }}>
      {Array.from({ length: arcs }).map((_, i) => {
        const local = ease(clamp((wave - i * 0.3) % 1, 0, 1))
        return (
          <div key={i} style={{
            position: 'absolute', right: -8 - i * 9, top: '50%', width: 17, height: 26 + i * 10,
            marginTop: -(13 + i * 5), border: '2.5px solid rgba(var(--amber-rgb),0.7)',
            borderRadius: '50%', clipPath: 'inset(0 0 0 55%)',
            opacity: local * (0.85 - i * 0.12), transform: `scale(${lerp(0.7, 1.1, local)})`,
          }} />
        )
      })}
      <div style={{ position: 'absolute', left: 0, top: 17, width: 9, height: 9, borderRadius: 'var(--radius-full)', background: 'linear-gradient(135deg,#3a3a42,#0e0e12)' }} />
      <div style={{ position: 'absolute', left: 7, top: 0, width: 46, height: 43, background: 'linear-gradient(100deg,#2c2c34,#0c0c10 60%)', clipPath: 'polygon(0 42%, 0 58%, 100% 100%, 100% 0)' }} />
      <div style={{ position: 'absolute', right: -2, top: 2, width: 10, height: 39, borderRadius: 'var(--radius-pill)', border: '3.5px solid var(--gold-deep)', background: 'radial-gradient(circle,#1c1c22,#0c0c10)', boxShadow: '0 0 8px rgba(212,175,55,0.4)' }} />
      <div style={{ position: 'absolute', left: 20, top: 34, width: 19, height: 12, border: '3px solid var(--gold-deep)', borderTop: 'none', borderRadius: '0 0 7px 7px' }} />
      <div style={{
        position: 'absolute', right: -64, top: '50%', marginTop: -11,
        fontFamily: SERIF, fontWeight: 600, fontSize: 19, color: AMBER, whiteSpace: 'nowrap',
        opacity: cutIn,
        transform: `translate(${jx}px, ${jy}px) scale(${lerp(0.6, 1, cutIn)}) rotate(${4 + jr}deg)`,
        textShadow: '0 0 10px rgba(var(--amber-rgb),0.5)',
      }}>Cut!</div>
    </div>
  )
}

function Trophy({ p, shine, orbitSpeed }) {
  const rise = ease(p)
  const orbitRot = shine * 360 * orbitSpeed
  return (
    <div style={{ position: 'relative', width: 168, height: 226, transform: `translateY(${lerp(36, 0, rise)}px) scale(${lerp(0.7, 1, rise)})`, opacity: rise }}>
      <div style={{ position: 'absolute', bottom: -6, left: '50%', width: 100, height: 14, marginLeft: -50, borderRadius: '50%', background: 'radial-gradient(ellipse, rgba(0,0,0,0.5), transparent 70%)' }} />
      <div style={{ position: 'absolute', bottom: 0, left: 22, right: 22, height: 13, borderRadius: 3, background: 'linear-gradient(180deg,#f3d879,#8a6c1f)', boxShadow: 'inset 0 -3px 4px rgba(0,0,0,0.35)' }} />
      <div style={{ position: 'absolute', bottom: 12, left: 36, right: 36, height: 11, borderRadius: 3, background: 'linear-gradient(180deg,#fff0c0,#c8a028)', boxShadow: 'inset 0 -2px 3px rgba(0,0,0,0.3)' }} />
      <div style={{ position: 'absolute', bottom: 21, left: 46, right: 46, height: 30, borderRadius: 4, background: 'linear-gradient(160deg,#2a1c05,#140d02)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(var(--amber-rgb),0.4)' }}>
        <span style={{ color: GOLD, fontSize: 7, fontWeight: 700, letterSpacing: '0.14em' }}>COMPLETE</span>
      </div>
      <div style={{ position: 'absolute', bottom: 48, left: '50%', width: 8, height: 56, marginLeft: -4, background: 'linear-gradient(90deg,#8a6c1f,#fff0c0 45%,#c8a028)', borderRadius: 3 }} />
      <div style={{ position: 'absolute', bottom: 98, left: '50%', width: 86, height: 86, marginLeft: -43 }}>
        <div style={{
          position: 'absolute', inset: 4, borderRadius: 'var(--radius-full)',
          background: 'radial-gradient(circle at 32% 28%, #fffbe6 0%, #fdeaa0 18%, #e8bc3f 48%, #a5801e 85%, #7a5f16 100%)',
          boxShadow: '0 8px 22px rgba(0,0,0,0.45), inset -6px -8px 14px rgba(0,0,0,0.25)', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: 0, bottom: 0, left: 0, width: '50%',
            background: 'linear-gradient(100deg, transparent, rgba(var(--ink-rgb),0.7), transparent)',
            transform: `translateX(${lerp(-82, 128, shine)}px) skewX(-14deg)`,
          }} />
        </div>
        <div style={{ position: 'absolute', inset: -6, border: `2.5px solid ${GOLD}`, borderRadius: 'var(--radius-full)', boxShadow: '0 2px 6px rgba(0,0,0,0.3)', transform: `rotate(${orbitRot}deg) rotateX(72deg)` }} />
        <div style={{ position: 'absolute', inset: -6, border: '2.5px solid #fff0c0', borderRadius: 'var(--radius-full)', boxShadow: '0 2px 6px rgba(0,0,0,0.3)', transform: `rotate(${-orbitRot * 0.7}deg) rotateX(72deg) rotateZ(60deg)` }} />
      </div>
    </div>
  )
}

function StatBlock({ value, label, unit, p }) {
  const shown = Math.round(lerp(0, value, ease(p)))
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 100, opacity: p > 0.02 ? 1 : 0 }}>
      <div style={{ fontFamily: SERIF, color: AMBER, fontSize: 42, fontWeight: 500 }}>{shown}{unit}</div>
      <div style={{ color: 'rgba(var(--text-rgb),0.6)', fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 6, textAlign: 'center' }}>{label}</div>
    </div>
  )
}

function Chip({ label, delay, p, onClick }) {
  const local = ease(seg(p, delay, delay + 0.3))
  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(162,155,254,0.12)', border: '1px solid rgba(162,155,254,0.3)',
      borderRadius: 'var(--radius-pill)', padding: '10px 18px', color: PURPLE, fontWeight: 600, fontSize: 14,
      opacity: local, transform: `translateY(${lerp(10, 0, local)}px)`, cursor: onClick ? 'pointer' : 'default',
    }}>{label}</div>
  )
}

// ── Scenes ───────────────────────────────────────────────────────────────────
function OpenScene({ progress, scale }) {
  const p = ease(seg(progress, 0.08, 0.55))
  const chair = seg(progress, 0.05, 0.65)
  const mega = seg(progress, 0.28, 0.75)
  const wave = seg(progress, 0.4, 1) * 2.4
  return (
    <Frame opacity={1}>
      <div style={{ position: 'relative', marginBottom: 20 }}>
        <DirectorsChair p={chair} />
        <div style={{ position: 'absolute', top: -30, right: -66 }}>
          <Megaphone p={mega} wave={wave} arcs={scale.waveArcs} />
        </div>
      </div>
      <div style={{ color: AMBER, fontWeight: 700, fontSize: 18, letterSpacing: '0.3em', textTransform: 'uppercase', opacity: p, transform: `translateY(${lerp(14, 0, p)}px)` }}>
        Universe Complete
      </div>
    </Frame>
  )
}

function TitleScene({ progress, canonName, finalTitle }) {
  const title = ease(seg(progress, 0.06, 0.4))
  const sub = ease(seg(progress, 0.32, 0.62))
  const exit = 1 - ease(seg(progress, 0.85, 1))
  return (
    <Frame opacity={exit}>
      <div style={{ color: GOLD, fontSize: 13, fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase', marginBottom: 16, opacity: title, transform: `translateY(${lerp(16, 0, title)}px)` }}>
        Every story, watched
      </div>
      <div style={{ fontFamily: SERIF, color: '#fff', fontSize: 44, fontWeight: 500, textAlign: 'center', padding: '0 40px', lineHeight: 1.15, opacity: title, transform: `translateY(${lerp(24, 0, title)}px)` }}>
        {canonName}
      </div>
      <div style={{ marginTop: 22, color: 'rgba(var(--text-rgb),0.6)', fontSize: 15, opacity: sub, transform: `translateY(${lerp(12, 0, sub)}px)`, textAlign: 'center', padding: '0 40px' }}>
        Last stop: {finalTitle}
      </div>
    </Frame>
  )
}

function StatsScene({ progress, titles, seasons, hours, statSpeed }) {
  const wrap = ease(seg(progress, 0.03, 0.2))
  const exit = 1 - ease(seg(progress, 0.86, 1))
  const p1 = seg(progress, 0.1, 0.1 + 0.45 / statSpeed)
  const p2 = seg(progress, 0.2, 0.2 + 0.45 / statSpeed)
  const p3 = seg(progress, 0.3, 0.3 + 0.45 / statSpeed)
  return (
    <Frame opacity={Math.min(wrap, exit)}>
      <div style={{ color: AMBER, fontSize: 12, fontWeight: 700, letterSpacing: '0.24em', textTransform: 'uppercase', marginBottom: 32 }}>The full tally</div>
      <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', justifyContent: 'center' }}>
        <StatBlock value={titles} label="Titles" unit="" p={p1} />
        <StatBlock value={seasons} label="Seasons" unit="" p={p2} />
        <StatBlock value={hours} label="Hours" unit="h" p={p3} />
      </div>
    </Frame>
  )
}

function PostersScene({ progress, shows, stagger }) {
  const wrap = ease(seg(progress, 0.02, 0.14))
  const exit = 1 - ease(seg(progress, 0.88, 1))
  const developed = seg(progress, 0.1, 0.85)
  return (
    <Frame opacity={Math.min(wrap, exit)}>
      <div style={{ color: 'rgba(var(--text-rgb),0.55)', fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 22 }}>Every entry, in order</div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 340 }}>
        {shows.map((s, i) => (
          <Poster key={s.id ?? i} show={s} developed={developed} delay={i * stagger} />
        ))}
      </div>
    </Frame>
  )
}

function SealScene({ progress, canonName, orbitSpeed }) {
  const rise = seg(progress, 0.1, 0.5)
  const wash = ease(seg(progress, 0.0, 0.15))
  const exit = 1 - ease(seg(progress, 0.85, 1))
  const shine = seg(progress, 0.4, 1)
  const label = ease(seg(progress, 0.55, 0.78))
  return (
    <Frame opacity={Math.min(wash, exit)}>
      <Trophy p={rise} shine={shine} orbitSpeed={orbitSpeed} />
      {/* Eyebrow — the same medallion+title beat as AchievementUnlock, so this
          reads as part of the family (2026-08-15 notification/celebration audit).
          Everything else in this 6-scene sequence stays bespoke by design. */}
      <div style={{ marginTop: 12, color: GOLD, fontSize: 10, fontWeight: 800, letterSpacing: '0.22em', textTransform: 'uppercase', opacity: label }}>
        Fully Canonized
      </div>
      <div style={{ marginTop: 6, color: '#fff', fontFamily: SERIF, fontSize: 18, fontWeight: 500, opacity: label, transform: `translateY(${lerp(10, 0, label)}px)`, textAlign: 'center', padding: '0 40px' }}>
        {canonName}
      </div>
    </Frame>
  )
}

function NextScene({ progress, otherCanons, onPick }) {
  const wrap = ease(seg(progress, 0.04, 0.22))
  const cta = ease(seg(progress, 0.5, 0.72))
  if (!otherCanons.length) {
    return (
      <Frame opacity={wrap}>
        <div style={{ color: 'rgba(var(--text-rgb),0.6)', fontSize: 15, marginBottom: 8 }}>Every canon.</div>
        <div style={{ fontFamily: SERIF, color: '#fff', fontSize: 28, fontWeight: 500, marginBottom: 20, textAlign: 'center' }}>Fully watched.</div>
        <div style={{ color: 'rgba(var(--text-rgb),0.5)', fontSize: 13, opacity: cta, textAlign: 'center', padding: '0 40px' }}>
          There isn't a franchise left to finish. That's the whole shelf.
        </div>
      </Frame>
    )
  }
  return (
    <Frame opacity={wrap}>
      <div style={{ color: 'rgba(var(--text-rgb),0.6)', fontSize: 15, marginBottom: 8 }}>One universe down.</div>
      <div style={{ fontFamily: SERIF, color: '#fff', fontSize: 28, fontWeight: 500, marginBottom: 24 }}>Where to next?</div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 28, flexWrap: 'wrap', justifyContent: 'center', padding: '0 30px' }}>
        {otherCanons.map((c, i) => (
          <Chip key={c.id} label={c.title} delay={0.05 + i * 0.13} p={progress} onClick={() => onPick(c)} />
        ))}
      </div>
      <div onClick={() => onPick(otherCanons[0])} style={{
        background: AMBER, borderRadius: 'var(--radius)', padding: '14px 34px', color: 'var(--amber-ink)', fontWeight: 800, fontSize: 15,
        opacity: cta, transform: `translateY(${lerp(10, 0, cta)}px)`, cursor: 'pointer',
      }}>
        Begin your next universe →
      </div>
    </Frame>
  )
}

function Frame({ children, opacity }) {
  return (
    <div style={{ position: 'absolute', inset: 0, background: BG, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: FONT, opacity }}>
      {children}
    </div>
  )
}

// ── Reduced-motion static summary ────────────────────────────────────────────
function StaticSummary({ data, onPick }) {
  const { canon, showCount, totalSeasons, totalMinutes, finalTitle, otherCanons } = data
  const hours = Math.round((totalMinutes || 0) / 60)
  return (
    <Frame opacity={1}>
      <div style={{ color: AMBER, fontWeight: 700, fontSize: 12, letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: 18 }}>Universe Complete</div>
      <div style={{ fontFamily: SERIF, color: '#fff', fontSize: 32, fontWeight: 500, textAlign: 'center', padding: '0 40px', marginBottom: 10 }}>{canon.title}</div>
      <div style={{ color: 'rgba(var(--text-rgb),0.6)', fontSize: 14, marginBottom: 22, textAlign: 'center', padding: '0 40px' }}>Last stop: {finalTitle}</div>
      <div style={{ display: 'flex', gap: 22, marginBottom: 26 }}>
        <StatBlock value={showCount} label="Titles" unit="" p={1} />
        <StatBlock value={totalSeasons} label="Seasons" unit="" p={1} />
        <StatBlock value={hours} label="Hours" unit="h" p={1} />
      </div>
      {otherCanons?.length > 0 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', padding: '0 30px' }}>
          {otherCanons.map(c => <Chip key={c.id} label={c.title} delay={0} p={1} onClick={() => onPick(c)} />)}
        </div>
      )}
    </Frame>
  )
}

// ── Root component ───────────────────────────────────────────────────────────
export function CanonSeal() {
  const [data, setData] = useState(null)
  const [playing, setPlaying] = useState(false)
  const timers = useRef([])
  const navigate = useNavigate()
  const clear = () => { timers.current.forEach(clearTimeout); timers.current = [] }
  const later = (fn, ms) => { const t = setTimeout(fn, ms); timers.current.push(t) }
  const motion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail
      if (!detail || !detail.canon) return
      // Wait for the wrap / Final Cut the same finale fired to finish (2026-10-11
      // audit: the seal used to play UNDER the series wrap at the same z-index).
      if (isOverlayBusy()) { later(() => handler(e), 400); return }
      clear()
      const holdMs = motion ? 5000 : TOTAL_MS + 3000
      window.__nextepWrapUntil = Date.now() + holdMs
      setData({ ...detail, otherCanons: detail.otherCanons || [] })
      logEvent('cinematic_shown', { kind: 'canonseal', canon: detail.canon?.slug || detail.canon?.id || null })
      setPlaying(!motion)
      if (motion) {
        // Reduced motion: collapse to one short static summary, no scene sequence.
        later(() => finish(), 4400)
      } else {
        later(() => finish(), TOTAL_MS + 400)
      }
    }
    window.addEventListener('nextep:canonseal', handler)
    return () => { window.removeEventListener('nextep:canonseal', handler); clear() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const seq = useSequence(playing)

  const finish = () => {
    clear()
    window.__nextepWrapUntil = 0
    window.dispatchEvent(new CustomEvent('nextep:wrap-done'))
    setPlaying(false)
    setData(null)
  }

  const onPick = (c) => {
    if (!c) return
    finish()
    navigate(`/canon/${c.id}`)
  }

  if (!data) return null
  const { canon, showCount, totalSeasons, totalMinutes, snapshot, finalTitle, otherCanons } = data
  const scale = scaleFor(showCount)
  const hours = Math.round((totalMinutes || 0) / 60)

  // Root fade-in is driven from `elapsed` (JS-computed), not a CSS @keyframes
  // animation — CSS animations (like requestAnimationFrame) are throttled to a
  // dead stop in backgrounded/hidden browser tabs, which would leave the whole
  // overlay stuck invisible if the sequence starts while the tab isn't focused.
  const rootFade = Math.min(1, seq.elapsed / 400)
  return (
    <div onClick={finish} style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-celebration)', background: BG, overflow: 'hidden', cursor: 'pointer', opacity: motion ? 1 : rootFade }}>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 50% 40%, rgba(var(--amber-rgb),0.08), transparent 62%)' }} />

      {motion ? (
        <StaticSummary data={data} onPick={onPick} />
      ) : (
        <>
          {seq.sceneKey === 'open'    && <OpenScene    progress={seq.progress} scale={scale} />}
          {seq.sceneKey === 'title'   && <TitleScene   progress={seq.progress} canonName={canon.title} finalTitle={finalTitle || '—'} />}
          {seq.sceneKey === 'stats'   && <StatsScene   progress={seq.progress} titles={showCount} seasons={totalSeasons || 0} hours={hours} statSpeed={scale.statSpeed} />}
          {seq.sceneKey === 'posters' && <PostersScene progress={seq.progress} shows={snapshot || []} stagger={scale.posterStagger} />}
          {seq.sceneKey === 'seal'    && <SealScene    progress={seq.progress} canonName={canon.title} orbitSpeed={scale.orbitSpeed} />}
          {seq.sceneKey === 'next'    && <NextScene    progress={seq.progress} otherCanons={otherCanons} onPick={onPick} />}
        </>
      )}

      <div style={{ position: 'absolute', bottom: 'max(20px, env(safe-area-inset-bottom))', left: 0, right: 0, textAlign: 'center', color: 'rgba(var(--ink-rgb),0.3)', fontSize: 10, letterSpacing: '0.06em', pointerEvents: 'none' }}>
        Tap to skip
      </div>
    </div>
  )
}
